import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { User, Globe, Database, Server, Save, Wifi } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Loading, StatRow } from "@/components/Common";
import { fmtClock, EUR } from "@/lib/format";
import api from "@/lib/api";

function StatusDot({ ok, label }) {
  const color = ok === "online" || ok === "connected" ? "#10B981" : ok === "syncing" ? "#D4AF37" : "#EF4444";
  return (
    <div className="flex items-center justify-between border-b border-white/5 py-2.5 last:border-0">
      <span className="font-sans text-[13px] text-zinc-400">{label}</span>
      <span className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ background: color }} />
        <span className="font-mono text-xs uppercase tracking-wider" style={{ color }}>{ok}</span>
      </span>
    </div>
  );
}

export default function Settings() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const [form, setForm] = useState({ currency: "EUR", timezone: "Europe/Rome", refresh_frequency: 5 });

  useEffect(() => { if (data?.settings) setForm({ currency: data.settings.currency, timezone: data.settings.timezone, refresh_frequency: data.settings.refresh_frequency }); }, [data]);
  if (isLoading || !data) return <Loading />;
  const sync = data.sync || {};

  const save = async () => {
    try { await api.put("/settings", form); toast.success("Impostazioni salvate"); refetch(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Profilo, trading, dati e connessione MT5" />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="dlx-card p-5 sm:p-6">
          <div className="mb-4 flex items-center gap-2"><User size={15} className="text-gold" /><span className="font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Profilo</span></div>
          {(data.users || []).map((u) => (
            <div key={u.id} className="flex items-center justify-between border-b border-white/5 py-3 last:border-0">
              <div>
                <div className="font-sans text-sm text-white">{u.name}</div>
                <div className="font-mono text-[11px] text-zinc-500">{u.email}</div>
              </div>
              <span className="rounded-full border border-gold/25 bg-gold/5 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-gold">{u.role}</span>
            </div>
          ))}
        </div>

        <div className="dlx-card p-5 sm:p-6">
          <div className="mb-4 flex items-center gap-2"><Globe size={15} className="text-gold" /><span className="font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Trading</span></div>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block font-sans text-[11px] text-zinc-400">Valuta</label>
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="settings-currency"
                className="w-full rounded-lg border border-white/10 bg-[#141418] px-3 py-2.5 font-sans text-sm text-white outline-none focus:border-gold/40">
                <option>EUR</option><option>USD</option><option>GBP</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block font-sans text-[11px] text-zinc-400">Timezone</label>
              <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} data-testid="settings-timezone"
                className="w-full rounded-lg border border-white/10 bg-[#141418] px-3 py-2.5 font-sans text-sm text-white outline-none focus:border-gold/40">
                <option>Europe/Rome</option><option>Europe/London</option><option>America/New_York</option><option>UTC</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block font-sans text-[11px] text-zinc-400">Frequenza aggiornamento (secondi)</label>
              <input type="number" value={form.refresh_frequency} onChange={(e) => setForm({ ...form, refresh_frequency: Number(e.target.value) })} data-testid="settings-refresh"
                className="w-full rounded-lg border border-white/10 bg-[#141418] px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-gold/40" />
            </div>
            <button onClick={save} data-testid="settings-save"
              className="flex items-center gap-2 rounded-lg px-5 py-2.5 font-sans text-sm font-semibold text-black transition-all hover:brightness-110 active:scale-95"
              style={{ background: "linear-gradient(135deg,#BF953F,#FCF6BA 45%,#AA771C)" }}>
              <Save size={15} /> Salva
            </button>
          </div>
        </div>

        <div className="dlx-card p-5 sm:p-6">
          <div className="mb-4 flex items-center gap-2"><Database size={15} className="text-gold" /><span className="font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Dati</span></div>
          <StatRow label="Modalità" value={sync.mode || "DEMO"} tone={sync.mode === "LIVE" ? "profit" : undefined} />
          <StatRow label="Ultimo Sync" value={fmtClock(sync.last_sync)} />
          <StatRow label="Record ricevuti" value={sync.records_received ?? 0} />
          <StatRow label="Broker" value={data.account?.broker || "—"} />
          <StatRow label="Conto" value={data.account?.account_number || "—"} />
          <StatRow label="Balance" value={EUR(data.account?.balance)} />
        </div>

        <div className="dlx-card p-5 sm:p-6" data-testid="mt5-connection-panel">
          <div className="mb-4 flex items-center gap-2"><Server size={15} className="text-gold" /><span className="font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Connessione MT5</span></div>
          <StatusDot ok={sync.mt5_status || "disconnected"} label="MT5 Connection Status" />
          <StatusDot ok={sync.bridge_status || "offline"} label="Bridge Status" />
          <StatusDot ok={sync.api_status || "online"} label="API Status" />
          <StatRow label="Last Sync" value={fmtClock(sync.last_sync)} />
          {sync.last_error && <div className="mt-2 rounded-lg border border-loss/25 bg-loss/5 px-3 py-2 font-mono text-[11px] text-loss">{sync.last_error}</div>}
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-gold/15 bg-gold/[0.03] px-3 py-3">
            <Wifi size={14} className="mt-0.5 text-gold" />
            <p className="font-sans text-[11px] leading-relaxed text-zinc-400">
              Endpoint pronto: <span className="font-mono text-gold">POST /api/mt5/sync</span>. Avvia il Python Bridge sul VPS con MT5 per passare automaticamente da <span className="text-gold">DEMO</span> a <span className="text-profit">LIVE</span>. Il token resta solo nel backend.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
